import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  clampPdfViewportScale,
  clampViewportScale,
  clientToContentPoint,
  clientToSvgUserPoint,
  useViewerController,
  viewerQueryKeys,
  zoomViewportAtPoint,
  type PanStartAnchor,
} from '@ironview/interactive-viewer';
import {
  PhotoMarker,
  Photo,
  Unit,
  Layout,
  MarkerAttributes,
  Position,
  ImageDimensions,
  Photo360Response,
  PhotoSelectionTooltipElement,
} from '../types/types';
import { useLayouts, useLayoutData, usePhotoData, useSavePhotoMarkers } from './hooks';
import { useCreateViewerAreaTag } from './useAreaTagCommands';
import { invalidateLayoutHotspots, useLayoutHotspots } from './useLayoutHotspots';
import { useToast } from '@/shared/hooks/use-toast';
import { ironview360ViewerAdapter } from '../adapters/ironview360DataAdapter';
import { useMarkerAutosave } from './useMarkerAutosave';
import { getLayoutAssetKey } from '../utils/layoutAssetKey';

export interface InteractiveViewerState {
  // Core UI state
  mode: 'view' | 'measure' | 'photoIndex' | 'areaTags';
  scale: number;
  position: Position;
  isDragging: boolean;
  selectedUnit: Unit | null;

  // Layout state
  selectedLayoutId: string;
  imagePath: string;
  pdfPath: string | null;
  pdfPageNumber: number;
  pdfCropRegion: [number, number, number, number] | null;
  flippedHorizontal: boolean;
  flippedVertical: boolean;
  isPdfLayout: boolean;
  imageDimensions: ImageDimensions;
  isLayoutAssetLoading: boolean;

  // Area creation state
  currentPolygon: number[][];
  showAreaForm: boolean;
  areaName: string;
  selectedPhotos: number[];

  // Photo markers state
  photoIndexMode: boolean;
  localPhotoMarkers: PhotoMarker[];
  currentMarkerNumber: number;
  selectedMarkers: Set<number>;
  lastSelectedMarkerId: number | null; // For range selection with SHIFT
  copiedMarkers: PhotoMarker[]; // For copy/paste functionality
  markerAttributes: MarkerAttributes;
  maintainAspectRatio: boolean;
  markerAutoSaveEnabled: boolean;
  showMarkerToolbar: boolean;
  draggingMarkers: boolean;
  dragStartPosition: Position | null;
  markerDragOffsets: Map<number, Position>;
  justCompletedDrag: boolean;

  // Photo viewing state
  showPhotoCarousel: boolean;
  openedPhotoId: number | null;
  currentPhoto: Photo360Response | null;
  targetCoordinates: { pitch: number; yaw: number } | null;

  // Photo selection tooltip
  photoSelectionTooltip: PhotoSelectionTooltipElement;

  // Current marker photos for navigation
  currentMarkerPhotos: Photo[];
  currentPhotoIndex: number;

  // Measurement tools
  showCoordinates: boolean;
  isHorizontalConstrained: boolean;
  isVerticalConstrained: boolean;

  // Context menu
  contextMenu: {
    visible: boolean;
    position: { x: number; y: number };
  };

  // Sequence change dialog
  sequenceChangeDialog: {
    visible: boolean;
    markerId: number | null;
  };

  // Adjust sequence mode
  adjustSequenceMode: boolean;
  adjustSequencePosition: { x: number; y: number } | null;

  // Undo history
  markerHistory: PhotoMarker[][];
  historyIndex: number;
}

export interface InteractiveViewerActions {
  // Mode management
  handleSetMode: (newMode: 'view' | 'measure' | 'photoIndex' | 'areaTags') => void;

  // Layout management
  setSelectedLayoutId: (id: string) => void;
  loadLayout: (layout: Layout) => Promise<void>;

  // Navigation and transformation
  zoom: (factor: number) => void;
  reset: () => void;
  setPosition: (position: Position) => void;
  setScale: (scale: number) => void;

  // State setters
  setIsDragging: (dragging: boolean) => void;
  setCurrentPolygon: (polygon: number[][]) => void;
  setShowAreaForm: (show: boolean) => void;
  setAreaName: (name: string) => void;
  setSelectedPhotos: (photos: number[]) => void;
  setPhotoIndexMode: (mode: boolean) => void;
  setCurrentMarkerNumber: (number: number) => void;
  setSelectedMarkers: (markers: Set<number>) => void;
  setLastSelectedMarkerId: (id: number | null) => void;
  setMarkerAttributes: (attributes: MarkerAttributes) => void;
  setMaintainAspectRatio: (maintain: boolean) => void;
  setMarkerAutoSaveEnabled: (enabled: boolean) => void;
  setShowMarkerToolbar: (show: boolean) => void;
  setShowPhotoCarousel: (show: boolean) => void;
  setOpenedPhotoId: (id: number | null) => void;
  setCurrentPhotoIndex: (index: number) => void;
  setTargetCoordinates: (coords: { pitch: number; yaw: number } | null) => void;
  setPhotoSelectionTooltip: (tooltip: PhotoSelectionTooltipElement) => void;
  setSelectedUnit: (unit: Unit | null) => void;
  setShowCoordinates: (show: boolean) => void;
  setIsHorizontalConstrained: (constrained: boolean) => void;
  setIsVerticalConstrained: (constrained: boolean) => void;
  setImageDimensions: (dimensions: ImageDimensions) => void;
  completeLayoutAssetLoad: (layoutId: string) => void;

  // Area management
  closePolygon: () => void;
  clearPolygon: () => void;
  saveArea: () => Promise<void>;

  // Photo marker management
  addPhotoMarker: (point: Position) => void;
  deletePhotoMarker: (markerId: number) => void;
  deleteSelectedMarkers: () => void;
  selectPhotoMarker: (markerId: number, event: React.MouseEvent) => void;
  copySelectedMarkers: () => void;
  pasteMarkers: () => void;
  savePhotoMarkers: () => Promise<void>;
  handleMarkerRightClick: (event: React.MouseEvent) => void;
  closeContextMenu: () => void;
  openSequenceChangeDialog: () => void;
  closeSequenceChangeDialog: () => void;
  changeMarkerSequence: (newNumber: number) => void;
  setAdjustSequenceMode: (mode: boolean, position?: { x: number; y: number }) => void;
  incrementSelectedMarkers: () => void;
  decrementSelectedMarkers: () => void;
  handleMarkerDragStart: (markerId: number, event: React.MouseEvent) => void;
  handleMarkerDrag: (event: MouseEvent) => void;
  handleMarkerDragEnd: () => void;
  undo: () => void;
  saveToHistory: () => void;
  updateSelectedMarkers: (updates: Partial<PhotoMarker>) => void;

  // Photo viewing
  showPanorama: (photoId: number) => Promise<void>;
  handleViewHotspot: (
    hotspotId: number,
    photoId: number,
    coordinates: { pitch: number; yaw: number }
  ) => void;
  openDeepLinkTarget: (payload: {
    layoutId?: string;
    photoId?: number;
    hotspotId?: number;
    coordinates?: { pitch: number; yaw: number };
  }) => void;
  navigateToMarkerPhoto: (direction: 'prev' | 'next') => void;
  navigateToMarkerPhotoById: (photoId: number) => void;

  // Event handlers
  handleUnitClick: (unit: Unit, event: React.MouseEvent) => void;
  handleMarkerClick: (marker: PhotoMarker, event: React.MouseEvent) => void;

  // Utility functions
  getCursor: () => string;
  getScaledPoint: (e: React.MouseEvent) => Position | null;
  refreshPanorama: () => Promise<void>;
  refreshHotspots: () => Promise<void>;
}

type ViewerControllerStateKey =
  | 'mode'
  | 'scale'
  | 'position'
  | 'isDragging'
  | 'selectedLayoutId'
  | 'showPhotoCarousel'
  | 'openedPhotoId'
  | 'targetCoordinates';

type InteractiveViewerHostState = Omit<InteractiveViewerState, ViewerControllerStateKey>;

const VIEWER_CONTROLLER_STATE_KEYS: readonly ViewerControllerStateKey[] = [
  'mode',
  'scale',
  'position',
  'isDragging',
  'selectedLayoutId',
  'showPhotoCarousel',
  'openedPhotoId',
  'targetCoordinates',
];

const extractHostState = (state: InteractiveViewerState): InteractiveViewerHostState => {
  const hostState = { ...state } as Partial<InteractiveViewerState>;
  VIEWER_CONTROLLER_STATE_KEYS.forEach((key) => {
    delete hostState[key];
  });
  return hostState as InteractiveViewerHostState;
};

export const useInteractiveViewer = (layoutType?: string) => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const initialState = useMemo<InteractiveViewerState>(
    () => ({
      mode: 'view',
      scale: 1,
      position: { x: 0, y: 0 },
      isDragging: false,
      selectedUnit: null,
      selectedLayoutId: '',
      imagePath: '',
      pdfPath: null,
      pdfPageNumber: 1,
      pdfCropRegion: null,
      flippedHorizontal: false,
      flippedVertical: false,
      isPdfLayout: false,
      imageDimensions: { width: 0, height: 0 },
      isLayoutAssetLoading: false,
      currentPolygon: [],
      showAreaForm: false,
      areaName: '',
      selectedPhotos: [],
      photoIndexMode: false,
      localPhotoMarkers: [],
      currentMarkerNumber: 1,
      selectedMarkers: new Set(),
      lastSelectedMarkerId: null,
      copiedMarkers: [],
      markerAttributes: {
        width: 30,
        height: 18,
        fontSize: 12,
        aspectRatio: 30 / 18,
      },
      maintainAspectRatio: true,
      markerAutoSaveEnabled: false,
      showMarkerToolbar: false,
      draggingMarkers: false,
      dragStartPosition: null,
      markerDragOffsets: new Map(),
      justCompletedDrag: false,
      showPhotoCarousel: false,
      openedPhotoId: null,
      currentPhoto: null,
      targetCoordinates: null,
      photoSelectionTooltip: {
        visible: false,
        photos: [],
        position: { x: 0, y: 0 },
        message: undefined,
      },
      currentMarkerPhotos: [],
      currentPhotoIndex: 0,
      showCoordinates: false,
      isHorizontalConstrained: false,
      isVerticalConstrained: false,
      contextMenu: {
        visible: false,
        position: { x: 0, y: 0 },
      },
      sequenceChangeDialog: {
        visible: false,
        markerId: null,
      },
      adjustSequenceMode: false,
      adjustSequencePosition: null,
      markerHistory: [],
      historyIndex: -1,
    }),
    []
  );
  const {
    mode,
    setMode,
    scale,
    setScale,
    position,
    setPosition,
    isDragging,
    setIsDragging,
    selectedLayoutId,
    setSelectedLayoutId,
    isPanoramaOpen,
    setIsPanoramaOpen,
    openedPhotoId,
    setOpenedPhotoId,
    panoramaTarget,
    setPanoramaTarget,
  } = useViewerController<InteractiveViewerState['mode'], string, number>({
    initialMode: initialState.mode,
    initialLayoutId: initialState.selectedLayoutId,
  });
  const [hostState, setHostState] = useState<InteractiveViewerHostState>(() =>
    extractHostState(initialState)
  );
  const state = useMemo<InteractiveViewerState>(
    () => ({
      ...hostState,
      mode,
      scale,
      position,
      isDragging,
      selectedLayoutId,
      showPhotoCarousel: isPanoramaOpen,
      openedPhotoId,
      targetCoordinates: panoramaTarget,
    }),
    [
      hostState,
      isDragging,
      isPanoramaOpen,
      mode,
      openedPhotoId,
      panoramaTarget,
      position,
      scale,
      selectedLayoutId,
    ]
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const setState = useCallback(
    (update: React.SetStateAction<InteractiveViewerState>) => {
      const nextState = typeof update === 'function' ? update(stateRef.current) : update;
      stateRef.current = nextState;
      setMode(nextState.mode);
      setScale(nextState.scale);
      setPosition(nextState.position);
      setIsDragging(nextState.isDragging);
      setSelectedLayoutId(nextState.selectedLayoutId);
      setIsPanoramaOpen(nextState.showPhotoCarousel);
      setOpenedPhotoId(nextState.openedPhotoId);
      setPanoramaTarget(nextState.targetCoordinates);
      setHostState(extractHostState(nextState));
    },
    [
      setIsDragging,
      setIsPanoramaOpen,
      setMode,
      setOpenedPhotoId,
      setPanoramaTarget,
      setPosition,
      setScale,
      setSelectedLayoutId,
    ]
  );

  // Refs
  const svgRef = useRef<SVGSVGElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const dragStartRef = useRef<PanStartAnchor | null>(null);
  const lastDragPointRef = useRef<Position | null>(null);
  const dragStartTimeRef = useRef(0);
  const touchStartDistanceRef = useRef<number | null>(null);
  const touchStartScaleRef = useRef<number>(1);
  // Tracks image load retry attempts per layout to recover from expired signed URLs
  const imageRetryCountRef = useRef<Map<string, number>>(new Map());
  const lastLoadedLayoutRef = useRef<{ key: string; retryVersion: number } | null>(null);
  const [layoutAssetRetryVersion, setLayoutAssetRetryVersion] = useState(0);

  // Helper function to get SVG point
  const getSVGPoint = useCallback((event: { clientX: number; clientY: number }) => {
    if (!svgRef.current || !groupRef.current) return null;

    const svgRect = svgRef.current.getBoundingClientRect();
    const transform = groupRef.current.getCTM();
    if (!transform) return null;

    const invertedTransform = transform.inverse();
    const point = svgRef.current.createSVGPoint();
    point.x = event.clientX - svgRect.left;
    point.y = event.clientY - svgRect.top;
    return point.matrixTransform(invertedTransform);
  }, []);

  // Data hooks
  const {
    data: layouts = [],
    isLoading: layoutsLoading,
    error: layoutsError,
    refetch: refetchLayouts,
  } = useLayouts(projectId, layoutType);
  const {
    areaTags: rawAreaTags,
    photoMarkers,
    availablePhotos,
    isLoading: layoutDataLoading,
    isFetching: layoutDataFetching,
  } = useLayoutData(state.selectedLayoutId, projectId);
  const { hotspots: layoutHotspots } = useLayoutHotspots(state.selectedLayoutId, projectId);
  const { photoData, hotspotsData, refetchPhoto, refetchHotspots } = usePhotoData(
    state.openedPhotoId ?? undefined,
    projectId
  );

  // Mutation hooks
  const createAreaTagMutation = useCreateViewerAreaTag(state.selectedLayoutId, projectId ?? '');
  const savePhotoMarkersMutation = useSavePhotoMarkers(projectId);

  // Computed values
  const areaTags: Unit[] = React.useMemo(() => {
    if (!rawAreaTags || !Array.isArray(rawAreaTags)) {
      return [];
    }

    return rawAreaTags
      .filter((areaTag: any) => areaTag && typeof areaTag === 'object' && areaTag.id)
      .map((areaTag: any) => {
        const safePoints = Array.isArray(areaTag.points) ? areaTag.points : [];
        const convertedPoints = safePoints
          .map((point: any) => {
            let x, y;
            if (Array.isArray(point) && point.length >= 2) {
              x = point[0];
              y = point[1];
            } else if (point && typeof point === 'object' && 'x' in point && 'y' in point) {
              x = point.x;
              y = point.y;
            } else {
              return null;
            }

            if (typeof x === 'number' && typeof y === 'number' && !isNaN(x) && !isNaN(y)) {
              return [x, y];
            } else {
              return null;
            }
          })
          .filter((point: number[] | null): point is number[] => point !== null);

        return {
          id: areaTag.id,
          points: convertedPoints,
          marker_ids: Array.isArray(areaTag.marker_ids) ? areaTag.marker_ids : [],
          images:
            areaTag.images && typeof areaTag.images === 'object'
              ? Object.keys(areaTag.images).reduce(
                  (acc, key) => {
                    const imgs = areaTag.images[key];
                    if (Array.isArray(imgs)) {
                      acc[key] = imgs
                        .filter((img) => img && typeof img === 'object' && img.id && img.name)
                        .map((img) => ({
                          id: img.id,
                          name: img.name,
                          date: '',
                          scan_type: '',
                        }));
                    }
                    return acc;
                  },
                  {} as Record<string, Photo[]>
                )
              : {},
          text_position: areaTag.text_position,
          text_size: areaTag.text_size,
          text_rotation: areaTag.text_rotation,
        };
      })
      .filter((unit) => unit.points.length >= 3);
  }, [rawAreaTags]);

  const displayPhotoMarkers = state.photoIndexMode ? state.localPhotoMarkers : photoMarkers;

  // Helper function to update state
  const updateState = useCallback((updates: Partial<InteractiveViewerState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Load layout function
  const loadLayout = useCallback(
    async (layout: Layout) => {
      // Detect if layout has PDF
      const hasPdf = Boolean(layout.has_pdf && layout.pdf_file_url);
      const hasImage = Boolean(!hasPdf && layout.image_url);

      // Always clear previous state first
      setState((prev) => ({
        ...prev,
        selectedUnit: null,
        currentPolygon: [],
        selectedMarkers: new Set(),
        currentMarkerPhotos: [],
        currentPhotoIndex: 0,
        imagePath: '', // Clear image path immediately
        pdfPath: null, // Clear PDF path immediately
        flippedHorizontal: Boolean(layout.flipped_horizontal),
        flippedVertical: Boolean(layout.flipped_vertical),
        isPdfLayout: false,
        isLayoutAssetLoading: hasPdf || hasImage,
      }));

      // If layout has neither photo nor PDF, set default dimensions for empty state
      if (!hasPdf && !hasImage) {
        console.warn('[loadLayout] No image_url or pdf_file_url found for layout:', layout);
        setState((prev) => ({
          ...prev,
          imageDimensions: { width: 2000, height: 1500 }, // Default dimensions for layouts without photos
          scale: 1,
          position: { x: 0, y: 0 },
          isLayoutAssetLoading: false,
        }));
        return;
      }

      // Handle PDF layout
      if (hasPdf) {
        setState((prev) => ({
          ...prev,
          pdfPath: layout.pdf_file_url!,
          pdfPageNumber: layout.pdf_page_number ?? 1,
          pdfCropRegion: layout.pdf_crop_region ?? null,
          flippedHorizontal: Boolean(layout.flipped_horizontal),
          flippedVertical: Boolean(layout.flipped_vertical),
          isPdfLayout: true,
          selectedLayoutId: String(layout.id),
        }));
        // PDF dimensions will be set by the shared PDF stage after it loads.
        // For now, set default dimensions that will be overridden
        setState((prev) => ({
          ...prev,
          imageDimensions: { width: 2000, height: 1500 },
          scale: 1,
          position: { x: 0, y: 0 },
        }));
        return;
      }

      // Decode image layouts before mounting the SVG scene. The SVG image then
      // reports its own paint completion while the loading overlay remains visible.
      const img = new Image();
      img.onload = async () => {
        try {
          await img.decode();
        } catch {
          // Some browsers resolve onload with no decode() support. The SVG image
          // load event remains the final readiness signal in that case.
        }
        imageRetryCountRef.current.delete(String(layout.id));
        setState((prev) => {
          if (prev.selectedLayoutId !== String(layout.id)) return prev;
          return {
            ...prev,
            imagePath: layout.image_url || '',
            imageDimensions: { width: img.naturalWidth, height: img.naturalHeight },
            scale: 1,
            position: { x: 0, y: 0 },
          };
        });
      };
      img.onerror = (error) => {
        const layoutKey = String(layout.id);
        const retryCount = imageRetryCountRef.current.get(layoutKey) || 0;

        if (retryCount < 1) {
          imageRetryCountRef.current.set(layoutKey, retryCount + 1);
          if (projectId) {
            void queryClient
              .invalidateQueries({
                queryKey: viewerQueryKeys.layoutLists(ironview360ViewerAdapter.id, projectId),
              })
              .finally(() => setLayoutAssetRetryVersion((version) => version + 1));
          }
          return;
        }

        console.error('Failed to load image after retry:', layout.image_url, error);
        setState((prev) => {
          if (prev.selectedLayoutId !== String(layout.id)) return prev;
          return {
            ...prev,
            imagePath: '',
            imageDimensions: { width: 2000, height: 1500 },
            scale: 1,
            position: { x: 0, y: 0 },
            isLayoutAssetLoading: false,
          };
        });
      };
      if (layout.image_url) {
        img.src = layout.image_url;
      }
    },
    [projectId, queryClient]
  );

  // Action callbacks - defined separately for useMemo dependencies
  const handleSetMode = useCallback(
    (newMode: InteractiveViewerState['mode']) => {
      updateState({ mode: newMode });
    },
    [updateState]
  );

  const setSelectedLayoutIdAction = useCallback((id: string) => {
    setState((prev) => {
      if (prev.selectedLayoutId === id) return prev;

      return {
        ...prev,
        selectedLayoutId: id,
        selectedUnit: null,
        imagePath: '',
        pdfPath: null,
        isPdfLayout: false,
        isLayoutAssetLoading: true,
      };
    });
  }, []);

  const zoomAction = useCallback(
    (factor: number) => {
      const nextScale = state.isPdfLayout
        ? clampPdfViewportScale(state.scale * factor)
        : clampViewportScale(state.scale * factor);
      if (nextScale === state.scale) return;

      let anchor: Position | null = null;
      const svg = svgRef.current;
      if (state.isPdfLayout) {
        const viewportRect = svg
          ?.closest<HTMLElement>('.iv-pdf-page-viewport')
          ?.getBoundingClientRect();
        if (viewportRect) {
          anchor = {
            x: viewportRect.width / 2,
            y: viewportRect.height / 2,
          };
        }
      } else if (svg) {
        const rect = svg.getBoundingClientRect();
        anchor = clientToSvgUserPoint(svg, rect.left + rect.width / 2, rect.top + rect.height / 2);
      }

      const transform = anchor
        ? zoomViewportAtPoint(state.position, state.scale, nextScale, anchor)
        : { scale: nextScale, position: state.position };
      updateState(transform);
    },
    [state.isPdfLayout, state.position, state.scale, updateState]
  );

  const resetAction = useCallback(() => {
    updateState({
      scale: 1,
      position: { x: 0, y: 0 },
      selectedUnit: null,
      showPhotoCarousel: false,
      currentPolygon: [],
    });
  }, [updateState]);

  const setPositionAction = useCallback(
    (position: Position) => {
      updateState({ position });
    },
    [updateState]
  );

  const setScaleAction = useCallback(
    (scale: number) => {
      updateState({ scale });
    },
    [updateState]
  );

  const setIsDraggingAction = useCallback(
    (dragging: boolean) => {
      updateState({ isDragging: dragging });
    },
    [updateState]
  );

  const setCurrentPolygonAction = useCallback(
    (polygon: number[][]) => {
      updateState({ currentPolygon: polygon });
    },
    [updateState]
  );

  const setShowAreaFormAction = useCallback(
    (show: boolean) => {
      updateState({ showAreaForm: show });
    },
    [updateState]
  );

  const setAreaNameAction = useCallback(
    (name: string) => {
      updateState({ areaName: name });
    },
    [updateState]
  );

  const setSelectedPhotosAction = useCallback(
    (photos: number[]) => {
      updateState({ selectedPhotos: photos });
    },
    [updateState]
  );

  const setPhotoIndexModeAction = useCallback(
    (mode: boolean) => {
      updateState({
        photoIndexMode: mode,
        markerHistory: mode ? state.markerHistory : [],
        historyIndex: mode ? state.historyIndex : -1,
        lastSelectedMarkerId: mode ? state.lastSelectedMarkerId : null,
      });
    },
    [state.markerHistory, state.historyIndex, state.lastSelectedMarkerId, updateState]
  );

  const setCurrentMarkerNumberAction = useCallback(
    (number: number) => {
      updateState({ currentMarkerNumber: number });
    },
    [updateState]
  );

  const setSelectedMarkersAction = useCallback(
    (markers: Set<number>) => {
      updateState({ selectedMarkers: markers });
    },
    [updateState]
  );

  const setLastSelectedMarkerIdAction = useCallback(
    (id: number | null) => {
      updateState({ lastSelectedMarkerId: id });
    },
    [updateState]
  );

  const setMarkerAttributesAction = useCallback(
    (attributes: InteractiveViewerState['markerAttributes']) => {
      updateState({ markerAttributes: attributes });
    },
    [updateState]
  );

  const setMaintainAspectRatioAction = useCallback(
    (maintain: boolean) => {
      updateState({ maintainAspectRatio: maintain });
    },
    [updateState]
  );

  const setMarkerAutoSaveEnabledAction = useCallback(
    (enabled: boolean) => {
      updateState({ markerAutoSaveEnabled: enabled });
    },
    [updateState]
  );

  const setShowMarkerToolbarAction = useCallback(
    (show: boolean) => {
      updateState({ showMarkerToolbar: show });
    },
    [updateState]
  );

  const setShowPhotoCarouselAction = useCallback(
    (show: boolean) => {
      updateState({ showPhotoCarousel: show });
    },
    [updateState]
  );

  const setOpenedPhotoIdAction = useCallback(
    (id: number | null) => {
      updateState({ openedPhotoId: id });
    },
    [updateState]
  );

  const setCurrentPhotoIndexAction = useCallback(
    (index: number) => {
      updateState({ currentPhotoIndex: index });
    },
    [updateState]
  );

  const setTargetCoordinatesAction = useCallback(
    (coords: { pitch: number; yaw: number } | null) => {
      updateState({ targetCoordinates: coords });
    },
    [updateState]
  );

  const setPhotoSelectionTooltipAction = useCallback(
    (tooltip: InteractiveViewerState['photoSelectionTooltip']) => {
      updateState({ photoSelectionTooltip: tooltip });
    },
    [updateState]
  );

  const setSelectedUnitAction = useCallback(
    (unit: Unit | null) => {
      updateState({ selectedUnit: unit });
    },
    [updateState]
  );

  const setShowCoordinatesAction = useCallback(
    (show: boolean) => {
      updateState({ showCoordinates: show });
    },
    [updateState]
  );

  const setIsHorizontalConstrainedAction = useCallback(
    (constrained: boolean) => {
      updateState({ isHorizontalConstrained: constrained });
    },
    [updateState]
  );

  const setIsVerticalConstrainedAction = useCallback(
    (constrained: boolean) => {
      updateState({ isVerticalConstrained: constrained });
    },
    [updateState]
  );

  const setImageDimensionsAction = useCallback(
    (dimensions: ImageDimensions) => {
      updateState({ imageDimensions: dimensions });
    },
    [updateState]
  );

  const completeLayoutAssetLoadAction = useCallback((layoutId: string) => {
    setState((prev) => {
      if (prev.selectedLayoutId !== layoutId || !prev.isLayoutAssetLoading) return prev;
      return { ...prev, isLayoutAssetLoading: false };
    });
  }, []);

  const closePolygonAction = useCallback(() => {
    if (state.currentPolygon.length >= 3) {
      updateState({ showAreaForm: true });
    } else {
      alert('Need at least 3 points to close polygon');
    }
  }, [state.currentPolygon.length, updateState]);

  const clearPolygonAction = useCallback(() => {
    updateState({
      currentPolygon: [],
      mode: 'view',
    });
  }, [updateState]);

  const saveAreaAction = useCallback(async () => {
    if (!state.areaName.trim()) {
      alert('Please enter an area name');
      return;
    }
    if (!projectId) {
      alert('A project is required to save units/locations');
      return;
    }

    try {
      const markerIds = state.localPhotoMarkers
        .filter((marker) => {
          const x = marker.x;
          const y = marker.y;
          let inside = false;

          for (
            let i = 0, j = state.currentPolygon.length - 1;
            i < state.currentPolygon.length;
            j = i++
          ) {
            const [xi, yi] = state.currentPolygon[i];
            const [xj, yj] = state.currentPolygon[j];
            const intersects =
              yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi;
            if (intersects) inside = !inside;
          }

          return inside;
        })
        .map((marker) => marker.dbId)
        .filter((id): id is number => typeof id === 'number');

      await createAreaTagMutation.mutateAsync({
        name: state.areaName,
        markerIds: [...new Set(markerIds)],
        photoIds: state.selectedPhotos,
        points: state.currentPolygon.map(([x, y]) => [x, y] as const),
      });

      updateState({
        showAreaForm: false,
        currentPolygon: [],
        areaName: '',
        selectedPhotos: [],
        mode: 'view',
      });
    } catch (error) {
      console.error('Error saving area:', error);
      alert('Failed to save area');
    }
  }, [
    projectId,
    state.areaName,
    state.selectedLayoutId,
    state.selectedPhotos,
    state.currentPolygon,
    state.localPhotoMarkers,
    createAreaTagMutation,
    updateState,
  ]);

  const addPhotoMarkerAction = useCallback(
    (point: Position) => {
      const newMarker: PhotoMarker = {
        id: state.currentMarkerNumber,
        number: state.currentMarkerNumber,
        x: point.x,
        y: point.y,
        photos: [],
        ...state.markerAttributes,
      };

      const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);
      const newHistory = [...currentHistory, [...state.localPhotoMarkers, newMarker]];

      updateState({
        localPhotoMarkers: [...state.localPhotoMarkers, newMarker],
        currentMarkerNumber: state.currentMarkerNumber + 1,
        showMarkerToolbar:
          state.photoIndexMode && !state.showMarkerToolbar ? true : state.showMarkerToolbar,
        markerHistory: newHistory,
        historyIndex: newHistory.length - 1,
      });
    },
    [
      state.currentMarkerNumber,
      state.localPhotoMarkers,
      state.markerAttributes,
      state.photoIndexMode,
      state.showMarkerToolbar,
      state.markerHistory,
      state.historyIndex,
      updateState,
    ]
  );

  const deletePhotoMarkerAction = useCallback(
    (markerId: number) => {
      const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);
      const newMarkers = state.localPhotoMarkers.filter((m) => m.id !== markerId);
      const newHistory = [...currentHistory, [...newMarkers]];

      updateState({
        localPhotoMarkers: newMarkers,
        selectedMarkers: new Set([...state.selectedMarkers].filter((id) => id !== markerId)),
        contextMenu: { visible: false, position: { x: 0, y: 0 } },
        markerHistory: newHistory,
        historyIndex: newHistory.length - 1,
      });
    },
    [
      state.localPhotoMarkers,
      state.selectedMarkers,
      state.markerHistory,
      state.historyIndex,
      updateState,
    ]
  );

  const deleteSelectedMarkersAction = useCallback(() => {
    if (state.selectedMarkers.size === 0) return;

    const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);
    const newMarkers = state.localPhotoMarkers.filter((m) => !state.selectedMarkers.has(m.id));
    const newHistory = [...currentHistory, [...newMarkers]];

    updateState({
      localPhotoMarkers: newMarkers,
      selectedMarkers: new Set(),
      lastSelectedMarkerId: null,
      contextMenu: { visible: false, position: { x: 0, y: 0 } },
      markerHistory: newHistory,
      historyIndex: newHistory.length - 1,
    });
  }, [
    state.localPhotoMarkers,
    state.selectedMarkers,
    state.markerHistory,
    state.historyIndex,
    updateState,
  ]);

  const copySelectedMarkersAction = useCallback(() => {
    if (state.selectedMarkers.size === 0) return;

    const markersToCopy = state.localPhotoMarkers.filter((m) => state.selectedMarkers.has(m.id));

    updateState({ copiedMarkers: markersToCopy });

    // Show success toast
    toast({
      title: 'Markers copied',
      description: `${markersToCopy.length} marker${markersToCopy.length === 1 ? '' : 's'} copied to clipboard`,
      className: 'bg-green-500 text-white',
    });
  }, [state.selectedMarkers, state.localPhotoMarkers, updateState, toast]);

  const pasteMarkersAction = useCallback(() => {
    if (state.copiedMarkers.length === 0) return;

    const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);

    // Find the highest marker number to continue sequence
    const maxNumber = state.localPhotoMarkers.reduce((max, m) => Math.max(max, m.number), 0);

    // Create new markers with new IDs and numbers, but same positions and photos
    const newMarkers = state.copiedMarkers.map((marker, index) => ({
      ...marker,
      id: Date.now() + index, // Generate new unique ID
      number: maxNumber + index + 1, // Continue sequence from highest number
    }));

    const updatedMarkers = [...state.localPhotoMarkers, ...newMarkers];
    const newHistory = [...currentHistory, [...updatedMarkers]];

    updateState({
      localPhotoMarkers: updatedMarkers,
      selectedMarkers: new Set(newMarkers.map((m) => m.id)), // Select the newly pasted markers
      markerHistory: newHistory,
      historyIndex: newHistory.length - 1,
    });

    // Show success toast
    toast({
      title: 'Markers pasted',
      description: `${newMarkers.length} marker${newMarkers.length === 1 ? '' : 's'} pasted`,
      className: 'bg-green-500 text-white',
    });
  }, [
    state.copiedMarkers,
    state.localPhotoMarkers,
    state.markerHistory,
    state.historyIndex,
    updateState,
    toast,
  ]);

  const selectPhotoMarkerAction = useCallback(
    (markerId: number, event: React.MouseEvent) => {
      if (!state.photoIndexMode) return;

      const isShift = event.shiftKey;
      const isCtrl = event.ctrlKey || event.metaKey;

      const newSelectedMarkers = new Set(state.selectedMarkers);

      if (!isCtrl && !isShift) {
        newSelectedMarkers.clear();
      }

      if (newSelectedMarkers.has(markerId)) {
        newSelectedMarkers.delete(markerId);
      } else {
        newSelectedMarkers.add(markerId);
      }

      updateState({
        selectedMarkers: newSelectedMarkers,
        contextMenu: { visible: false, position: { x: 0, y: 0 } },
      });
    },
    [state.photoIndexMode, state.selectedMarkers, updateState]
  );

  const handleMarkerRightClickAction = useCallback(
    (event: React.MouseEvent) => {
      if (!state.photoIndexMode) return;

      event.preventDefault();
      event.stopPropagation();

      if (state.selectedMarkers.size > 0) {
        updateState({
          contextMenu: {
            visible: true,
            position: { x: event.clientX, y: event.clientY },
          },
        });
      }
    },
    [state.photoIndexMode, state.selectedMarkers.size, updateState]
  );

  const closeContextMenuAction = useCallback(() => {
    updateState({
      contextMenu: { visible: false, position: { x: 0, y: 0 } },
    });
  }, [updateState]);

  const openSequenceChangeDialogAction = useCallback(() => {
    const selectedMarkerId = Array.from(state.selectedMarkers)[0];

    if (selectedMarkerId !== undefined) {
      updateState({
        sequenceChangeDialog: {
          visible: true,
          markerId: selectedMarkerId,
        },
      });
    }
  }, [state.selectedMarkers, updateState]);

  const closeSequenceChangeDialogAction = useCallback(() => {
    updateState({
      sequenceChangeDialog: {
        visible: false,
        markerId: null,
      },
    });
  }, [updateState]);

  const changeMarkerSequenceAction = useCallback(
    (newNumber: number) => {
      const markerId = state.sequenceChangeDialog.markerId;
      if (!markerId) {
        return;
      }

      const marker = state.localPhotoMarkers.find((m) => m.id === markerId);
      if (!marker) {
        return;
      }

      const currentNumber = marker.number;
      const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);

      const updatedMarkers = state.localPhotoMarkers.map((m) => {
        if (m.id === markerId) {
          return { ...m, number: newNumber };
        } else if (currentNumber < newNumber) {
          if (m.number > currentNumber && m.number <= newNumber) {
            return { ...m, number: m.number - 1 };
          }
        } else if (currentNumber > newNumber) {
          if (m.number >= newNumber && m.number < currentNumber) {
            return { ...m, number: m.number + 1 };
          }
        }
        return m;
      });

      const newHistory = [...currentHistory, [...updatedMarkers]];

      updateState({
        localPhotoMarkers: updatedMarkers,
        markerHistory: newHistory,
        historyIndex: newHistory.length - 1,
        sequenceChangeDialog: {
          visible: false,
          markerId: null,
        },
      });
    },
    [
      state.sequenceChangeDialog,
      state.localPhotoMarkers,
      state.markerHistory,
      state.historyIndex,
      updateState,
    ]
  );

  const setAdjustSequenceModeAction = useCallback(
    (mode: boolean, position?: { x: number; y: number }) => {
      updateState({
        adjustSequenceMode: mode,
        adjustSequencePosition: position || null,
      });
    },
    [updateState]
  );

  const incrementSelectedMarkersAction = useCallback(() => {
    if (state.selectedMarkers.size === 0) return;

    const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);
    const markerIds = Array.from(state.selectedMarkers);

    // Simply increment the number on each selected marker by 1
    // Don't touch any other markers
    const updatedMarkers = state.localPhotoMarkers.map((m) => {
      if (markerIds.includes(m.id)) {
        return { ...m, number: m.number + 1 };
      }
      return m;
    });

    const newHistory = [...currentHistory, [...updatedMarkers]];
    updateState({
      localPhotoMarkers: updatedMarkers,
      markerHistory: newHistory,
      historyIndex: newHistory.length - 1,
    });
  }, [
    state.selectedMarkers,
    state.localPhotoMarkers,
    state.markerHistory,
    state.historyIndex,
    updateState,
  ]);

  const decrementSelectedMarkersAction = useCallback(() => {
    if (state.selectedMarkers.size === 0) return;

    const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);
    const markerIds = Array.from(state.selectedMarkers);

    // Simply decrement the number on each selected marker by 1
    // Don't touch any other markers
    const updatedMarkers = state.localPhotoMarkers.map((m) => {
      if (markerIds.includes(m.id)) {
        return { ...m, number: m.number - 1 };
      }
      return m;
    });

    const newHistory = [...currentHistory, [...updatedMarkers]];
    updateState({
      localPhotoMarkers: updatedMarkers,
      markerHistory: newHistory,
      historyIndex: newHistory.length - 1,
    });
  }, [
    state.selectedMarkers,
    state.localPhotoMarkers,
    state.markerHistory,
    state.historyIndex,
    updateState,
  ]);

  const handleMarkerDragStartAction = useCallback(
    (markerId: number, event: React.MouseEvent) => {
      if (!state.photoIndexMode) return;

      const svgPoint = getSVGPoint(event);
      if (!svgPoint) return;

      let selectedMarkers = state.selectedMarkers;
      if (!selectedMarkers.has(markerId)) {
        selectedMarkers = new Set([markerId]);
      }

      const offsets = new Map<number, Position>();
      state.localPhotoMarkers.forEach((marker) => {
        if (selectedMarkers.has(marker.id)) {
          offsets.set(marker.id, {
            x: marker.x - svgPoint.x,
            y: marker.y - svgPoint.y,
          });
        }
      });

      updateState({
        selectedMarkers,
        dragStartPosition: { x: svgPoint.x, y: svgPoint.y },
        markerDragOffsets: offsets,
        contextMenu: { visible: false, position: { x: 0, y: 0 } },
      });
    },
    [state.photoIndexMode, state.selectedMarkers, state.localPhotoMarkers, getSVGPoint, updateState]
  );

  const handleMarkerDragAction = useCallback(
    (event: MouseEvent) => {
      if (!state.dragStartPosition) return;

      const svgPoint = getSVGPoint(event);
      if (!svgPoint) return;

      const deltaX = Math.abs(svgPoint.x - state.dragStartPosition.x);
      const deltaY = Math.abs(svgPoint.y - state.dragStartPosition.y);

      if (deltaX > 5 || deltaY > 5) {
        if (!state.draggingMarkers) {
          updateState({ draggingMarkers: true });
        }

        const updatedMarkers = state.localPhotoMarkers.map((marker) => {
          const offset = state.markerDragOffsets.get(marker.id);
          if (offset) {
            return {
              ...marker,
              x: Math.round(svgPoint.x + offset.x),
              y: Math.round(svgPoint.y + offset.y),
            };
          }
          return marker;
        });

        updateState({ localPhotoMarkers: updatedMarkers });
      }
    },
    [
      state.dragStartPosition,
      state.draggingMarkers,
      state.localPhotoMarkers,
      state.markerDragOffsets,
      getSVGPoint,
      updateState,
    ]
  );

  const handleMarkerDragEndAction = useCallback(() => {
    if (state.draggingMarkers) {
      const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);
      const newHistory = [...currentHistory, [...state.localPhotoMarkers]];

      updateState({
        draggingMarkers: false,
        dragStartPosition: null,
        markerDragOffsets: new Map(),
        justCompletedDrag: true,
        markerHistory: newHistory,
        historyIndex: newHistory.length - 1,
      });
    } else {
      updateState({
        draggingMarkers: false,
        dragStartPosition: null,
        markerDragOffsets: new Map(),
        justCompletedDrag: true,
      });
    }

    setTimeout(() => {
      updateState({ justCompletedDrag: false });
    }, 50);
  }, [
    state.draggingMarkers,
    state.markerHistory,
    state.historyIndex,
    state.localPhotoMarkers,
    updateState,
  ]);

  const saveToHistoryAction = useCallback(() => {
    const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);
    const newHistory = [...currentHistory, [...state.localPhotoMarkers]];

    updateState({
      markerHistory: newHistory,
      historyIndex: newHistory.length - 1,
    });
  }, [state.markerHistory, state.historyIndex, state.localPhotoMarkers, updateState]);

  const undoAction = useCallback(() => {
    if (state.historyIndex > 0) {
      const newIndex = state.historyIndex - 1;
      const previousState = state.markerHistory[newIndex];

      updateState({
        localPhotoMarkers: [...previousState],
        historyIndex: newIndex,
        selectedMarkers: new Set(),
        lastSelectedMarkerId: null,
      });
    }
  }, [state.historyIndex, state.markerHistory, updateState]);

  const updateSelectedMarkersAction = useCallback(
    (updates: Partial<PhotoMarker>) => {
      if (state.selectedMarkers.size === 0) return;

      const currentHistory = state.markerHistory.slice(0, state.historyIndex + 1);

      const updatedMarkers = state.localPhotoMarkers.map((marker) => {
        if (state.selectedMarkers.has(marker.id)) {
          return { ...marker, ...updates };
        }
        return marker;
      });

      const newHistory = [...currentHistory, [...updatedMarkers]];

      updateState({
        localPhotoMarkers: updatedMarkers,
        markerHistory: newHistory,
        historyIndex: newHistory.length - 1,
      });
    },
    [
      state.selectedMarkers,
      state.localPhotoMarkers,
      state.markerHistory,
      state.historyIndex,
      updateState,
    ]
  );

  const persistPhotoMarkers = useCallback(
    async (layoutId: string, markers: PhotoMarker[]) => {
      await savePhotoMarkersMutation.mutateAsync({
        layoutId,
        markers,
        markerAttributes: stateRef.current.markerAttributes,
      });
    },
    [savePhotoMarkersMutation]
  );

  const handleMarkerAutosaveError = useCallback(
    (error: unknown) => {
      console.error('Error auto-saving markers:', error);
      toast({
        title: 'Auto-save failed',
        description: 'Your marker changes are still open. Check your connection and try again.',
        variant: 'destructive',
      });
    },
    [toast]
  );

  const markerAutosave = useMarkerAutosave({
    active: state.photoIndexMode,
    enabled: state.markerAutoSaveEnabled,
    layoutId: state.selectedLayoutId,
    markers: state.localPhotoMarkers,
    save: persistPhotoMarkers,
    onError: handleMarkerAutosaveError,
  });

  const savePhotoMarkersAction = useCallback(async () => {
    const markersSnapshot = [...state.localPhotoMarkers];
    try {
      await persistPhotoMarkers(state.selectedLayoutId, markersSnapshot);

      markerAutosave.markSaved(markersSnapshot);
      alert('Photo markers saved successfully');
      updateState({
        photoIndexMode: false,
        showMarkerToolbar: false,
      });
    } catch (error) {
      console.error('Error saving markers:', error);
      alert('Failed to save markers');
    }
  }, [
    state.selectedLayoutId,
    state.localPhotoMarkers,
    persistPhotoMarkers,
    markerAutosave.markSaved,
    updateState,
  ]);

  const showPanoramaAction = useCallback(
    async (photoId: number) => {
      try {
        updateState({
          openedPhotoId: photoId,
          showPhotoCarousel: true,
        });
      } catch (error) {
        console.error('Error loading panorama:', error);
      }
    },
    [updateState]
  );

  const handleViewHotspotAction = useCallback(
    (_hotspotId: number, photoId: number, coordinates: { pitch: number; yaw: number }) => {
      if (photoId) {
        updateState({ targetCoordinates: { pitch: coordinates.pitch - 5, yaw: coordinates.yaw } });
        showPanoramaAction(photoId);
      }
    },
    [updateState, showPanoramaAction]
  );

  const openDeepLinkTargetAction = useCallback(
    (payload: {
      layoutId?: string;
      photoId?: number;
      hotspotId?: number;
      coordinates?: { pitch: number; yaw: number };
    }) => {
      const updates: Partial<InteractiveViewerState> = {};

      if (payload.layoutId) {
        updates.selectedLayoutId = payload.layoutId;
      }

      if (payload.photoId) {
        updates.openedPhotoId = payload.photoId;
        updates.showPhotoCarousel = true;
      }

      if (payload.coordinates) {
        // Keep behavior consistent with handleViewHotspotAction
        updates.targetCoordinates = {
          pitch: payload.coordinates.pitch - 5,
          yaw: payload.coordinates.yaw,
        };
      }

      if (Object.keys(updates).length > 0) {
        setState((prev) => {
          const layoutChanged = Boolean(
            payload.layoutId && payload.layoutId !== prev.selectedLayoutId
          );
          return {
            ...prev,
            ...updates,
            ...(layoutChanged
              ? {
                  selectedUnit: null,
                  imagePath: '',
                  pdfPath: null,
                  isPdfLayout: false,
                  isLayoutAssetLoading: true,
                }
              : {}),
          };
        });
      }
    },
    []
  );

  const navigateToMarkerPhotoAction = useCallback(
    (direction: 'prev' | 'next') => {
      if (availablePhotos.length <= 1) return;

      const currentIndex = availablePhotos.findIndex((p) => p.id === state.openedPhotoId);
      if (currentIndex === -1) return;

      let newIndex;
      if (direction === 'prev') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : availablePhotos.length - 1;
      } else {
        newIndex = currentIndex < availablePhotos.length - 1 ? currentIndex + 1 : 0;
      }

      const newPhoto = availablePhotos[newIndex];
      if (newPhoto) {
        updateState({
          openedPhotoId: newPhoto.id,
        });
      }
    },
    [availablePhotos, state.openedPhotoId, updateState]
  );

  const navigateToMarkerPhotoByIdAction = useCallback(
    (photoId: number) => {
      updateState({
        openedPhotoId: photoId,
        currentMarkerPhotos: [],
        currentPhotoIndex: 0,
      });
    },
    [updateState]
  );

  const handleUnitClickAction = useCallback(
    (unit: Unit, event: React.MouseEvent) => {
      event.stopPropagation();
      if (state.mode === 'view') {
        updateState({ selectedUnit: unit });
      }
    },
    [state.mode, updateState]
  );

  const handleMarkerClickAction = useCallback(
    (marker: PhotoMarker, event: React.MouseEvent) => {
      event.stopPropagation();

      if (state.photoIndexMode) {
        if (state.justCompletedDrag) {
          return;
        }

        const isShift = event.shiftKey;
        const isCtrl = event.ctrlKey || event.metaKey;

        const newSelectedMarkers = new Set(state.selectedMarkers);
        const wasSelected = newSelectedMarkers.has(marker.id);
        let newLastSelectedMarkerId: number | null = marker.id;

        if (isShift && state.lastSelectedMarkerId !== null) {
          // SHIFT + Click: Select range from last selected to current marker
          const lastMarker = state.localPhotoMarkers.find(
            (m) => m.id === state.lastSelectedMarkerId
          );

          if (lastMarker) {
            // Get the range of marker numbers to select
            const minNumber = Math.min(lastMarker.number, marker.number);
            const maxNumber = Math.max(lastMarker.number, marker.number);

            // Select all markers in the range
            state.localPhotoMarkers.forEach((m) => {
              if (m.number >= minNumber && m.number <= maxNumber) {
                newSelectedMarkers.add(m.id);
              }
            });

            // Keep the original last selected marker for further range selections
            newLastSelectedMarkerId = state.lastSelectedMarkerId;
          }
        } else if (isCtrl) {
          // CMD/CTRL + Click: Toggle individual marker selection
          if (wasSelected) {
            newSelectedMarkers.delete(marker.id);
            // If we're deselecting, don't update lastSelectedMarkerId
            newLastSelectedMarkerId = state.lastSelectedMarkerId;
          } else {
            newSelectedMarkers.add(marker.id);
          }
        } else {
          // Regular click: Single selection with toggle
          if (wasSelected && newSelectedMarkers.size === 1) {
            newSelectedMarkers.clear();
            newLastSelectedMarkerId = null;
          } else {
            newSelectedMarkers.clear();
            newSelectedMarkers.add(marker.id);
          }
        }

        let newMarkerAttributes = state.markerAttributes;
        if (newSelectedMarkers.size > 0) {
          const firstSelectedId = Array.from(newSelectedMarkers)[0];
          const firstSelectedMarker = state.localPhotoMarkers.find((m) => m.id === firstSelectedId);
          if (firstSelectedMarker) {
            newMarkerAttributes = {
              width: firstSelectedMarker.width,
              height: firstSelectedMarker.height,
              fontSize: firstSelectedMarker.fontSize,
              aspectRatio: firstSelectedMarker.width / firstSelectedMarker.height,
            };
          }
        }

        updateState({
          selectedMarkers: newSelectedMarkers,
          lastSelectedMarkerId: newLastSelectedMarkerId,
          markerAttributes: newMarkerAttributes,
          contextMenu: { visible: false, position: { x: 0, y: 0 } },
        });
      } else if (state.mode === 'view') {
        if (marker.photos.length === 1) {
          updateState({
            currentMarkerPhotos: marker.photos,
            currentPhotoIndex: 0,
            openedPhotoId: marker.photos[0].id,
            showPhotoCarousel: true,
          });
        } else if (marker.photos.length > 1) {
          const sortedPhotos = [...marker.photos].sort((a, b) => {
            const [monthA, dayA, yearA] = a.date.split('.');
            const [monthB, dayB, yearB] = b.date.split('.');

            const dateA = new Date(2000 + parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA));
            const dateB = new Date(2000 + parseInt(yearB), parseInt(monthB) - 1, parseInt(dayB));

            return dateB.getTime() - dateA.getTime();
          });

          updateState({ currentMarkerPhotos: sortedPhotos });

          const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
          updateState({
            photoSelectionTooltip: {
              visible: true,
              photos: sortedPhotos,
              message: undefined,
              position: {
                x: rect.left + rect.width / 2,
                y: rect.bottom + 5,
              },
            },
          });
        } else {
          const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
          updateState({
            photoSelectionTooltip: {
              visible: true,
              photos: [],
              message: 'No photos available for this area yet.',
              position: {
                x: rect.left + rect.width / 2,
                y: rect.bottom + 5,
              },
            },
          });
        }
      }
    },
    [
      state.mode,
      state.photoIndexMode,
      state.selectedMarkers,
      state.lastSelectedMarkerId,
      state.justCompletedDrag,
      state.localPhotoMarkers,
      state.markerAttributes,
      updateState,
    ]
  );

  const getCursorAction = useCallback(() => {
    if (state.isDragging) return 'grabbing';
    if (state.mode === 'measure' || state.photoIndexMode) return 'crosshair';
    if (state.mode === 'view' || state.mode === 'areaTags') return 'grab';
    return 'default';
  }, [state.isDragging, state.mode, state.photoIndexMode]);

  const getScaledPointAction = useCallback(
    (e: React.MouseEvent): Position | null => {
      if (!svgRef.current) return null;
      return clientToContentPoint(svgRef.current, e.clientX, e.clientY, {
        isPdfLayout: state.isPdfLayout,
        position: state.position,
        scale: state.scale,
      });
    },
    [state.isPdfLayout, state.position, state.scale]
  );

  const refreshHotspotsAction = useCallback(async () => {
    if (state.openedPhotoId) {
      await refetchHotspots();
      invalidateLayoutHotspots(queryClient, state.selectedLayoutId);
    }
  }, [state.openedPhotoId, state.selectedLayoutId, refetchHotspots, queryClient]);

  const refreshPanoramaAction = useCallback(async () => {
    if (!state.openedPhotoId) return;
    await refetchPhoto();
  }, [state.openedPhotoId, refetchPhoto]);

  // Actions object - wrapped in useMemo for stable reference
  const actions: InteractiveViewerActions = useMemo(
    () => ({
      handleSetMode,
      setSelectedLayoutId: setSelectedLayoutIdAction,
      loadLayout,
      zoom: zoomAction,
      reset: resetAction,
      setPosition: setPositionAction,
      setScale: setScaleAction,
      setIsDragging: setIsDraggingAction,
      setCurrentPolygon: setCurrentPolygonAction,
      setShowAreaForm: setShowAreaFormAction,
      setAreaName: setAreaNameAction,
      setSelectedPhotos: setSelectedPhotosAction,
      setPhotoIndexMode: setPhotoIndexModeAction,
      setCurrentMarkerNumber: setCurrentMarkerNumberAction,
      setSelectedMarkers: setSelectedMarkersAction,
      setLastSelectedMarkerId: setLastSelectedMarkerIdAction,
      setMarkerAttributes: setMarkerAttributesAction,
      setMaintainAspectRatio: setMaintainAspectRatioAction,
      setMarkerAutoSaveEnabled: setMarkerAutoSaveEnabledAction,
      setShowMarkerToolbar: setShowMarkerToolbarAction,
      setShowPhotoCarousel: setShowPhotoCarouselAction,
      setOpenedPhotoId: setOpenedPhotoIdAction,
      setCurrentPhotoIndex: setCurrentPhotoIndexAction,
      setTargetCoordinates: setTargetCoordinatesAction,
      setPhotoSelectionTooltip: setPhotoSelectionTooltipAction,
      setSelectedUnit: setSelectedUnitAction,
      setShowCoordinates: setShowCoordinatesAction,
      setIsHorizontalConstrained: setIsHorizontalConstrainedAction,
      setIsVerticalConstrained: setIsVerticalConstrainedAction,
      setImageDimensions: setImageDimensionsAction,
      completeLayoutAssetLoad: completeLayoutAssetLoadAction,
      closePolygon: closePolygonAction,
      clearPolygon: clearPolygonAction,
      saveArea: saveAreaAction,
      addPhotoMarker: addPhotoMarkerAction,
      deletePhotoMarker: deletePhotoMarkerAction,
      deleteSelectedMarkers: deleteSelectedMarkersAction,
      copySelectedMarkers: copySelectedMarkersAction,
      pasteMarkers: pasteMarkersAction,
      selectPhotoMarker: selectPhotoMarkerAction,
      handleMarkerRightClick: handleMarkerRightClickAction,
      closeContextMenu: closeContextMenuAction,
      openSequenceChangeDialog: openSequenceChangeDialogAction,
      closeSequenceChangeDialog: closeSequenceChangeDialogAction,
      changeMarkerSequence: changeMarkerSequenceAction,
      setAdjustSequenceMode: setAdjustSequenceModeAction,
      incrementSelectedMarkers: incrementSelectedMarkersAction,
      decrementSelectedMarkers: decrementSelectedMarkersAction,
      handleMarkerDragStart: handleMarkerDragStartAction,
      handleMarkerDrag: handleMarkerDragAction,
      handleMarkerDragEnd: handleMarkerDragEndAction,
      saveToHistory: saveToHistoryAction,
      undo: undoAction,
      updateSelectedMarkers: updateSelectedMarkersAction,
      savePhotoMarkers: savePhotoMarkersAction,
      showPanorama: showPanoramaAction,
      handleViewHotspot: handleViewHotspotAction,
      openDeepLinkTarget: openDeepLinkTargetAction,
      navigateToMarkerPhoto: navigateToMarkerPhotoAction,
      navigateToMarkerPhotoById: navigateToMarkerPhotoByIdAction,
      handleUnitClick: handleUnitClickAction,
      handleMarkerClick: handleMarkerClickAction,
      getCursor: getCursorAction,
      getScaledPoint: getScaledPointAction,
      refreshPanorama: refreshPanoramaAction,
      refreshHotspots: refreshHotspotsAction,
    }),
    [
      handleSetMode,
      setSelectedLayoutIdAction,
      loadLayout,
      zoomAction,
      resetAction,
      setPositionAction,
      setScaleAction,
      setIsDraggingAction,
      setCurrentPolygonAction,
      setShowAreaFormAction,
      setAreaNameAction,
      setSelectedPhotosAction,
      setPhotoIndexModeAction,
      setCurrentMarkerNumberAction,
      setSelectedMarkersAction,
      setMarkerAttributesAction,
      setMaintainAspectRatioAction,
      setMarkerAutoSaveEnabledAction,
      setShowMarkerToolbarAction,
      setShowPhotoCarouselAction,
      setOpenedPhotoIdAction,
      setCurrentPhotoIndexAction,
      setTargetCoordinatesAction,
      setPhotoSelectionTooltipAction,
      setSelectedUnitAction,
      setShowCoordinatesAction,
      setIsHorizontalConstrainedAction,
      setIsVerticalConstrainedAction,
      setImageDimensionsAction,
      completeLayoutAssetLoadAction,
      closePolygonAction,
      clearPolygonAction,
      saveAreaAction,
      addPhotoMarkerAction,
      deletePhotoMarkerAction,
      deleteSelectedMarkersAction,
      selectPhotoMarkerAction,
      handleMarkerRightClickAction,
      closeContextMenuAction,
      openSequenceChangeDialogAction,
      closeSequenceChangeDialogAction,
      changeMarkerSequenceAction,
      setAdjustSequenceModeAction,
      incrementSelectedMarkersAction,
      decrementSelectedMarkersAction,
      handleMarkerDragStartAction,
      handleMarkerDragAction,
      handleMarkerDragEndAction,
      saveToHistoryAction,
      undoAction,
      updateSelectedMarkersAction,
      savePhotoMarkersAction,
      showPanoramaAction,
      handleViewHotspotAction,
      openDeepLinkTargetAction,
      navigateToMarkerPhotoAction,
      navigateToMarkerPhotoByIdAction,
      handleUnitClickAction,
      handleMarkerClickAction,
      getCursorAction,
      getScaledPointAction,
      refreshPanoramaAction,
      refreshHotspotsAction,
    ]
  );

  // Effects
  // Reset selectedLayoutId when project or layout type changes
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      selectedLayoutId: '',
      selectedUnit: null,
      imagePath: '',
      pdfPath: null,
      isPdfLayout: false,
      isLayoutAssetLoading: false,
    }));
  }, [projectId, layoutType]);

  useEffect(() => {
    if (layouts.length > 0 && !state.selectedLayoutId) {
      const sortedLayouts = [...layouts].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
      );
      setSelectedLayoutIdAction(String(sortedLayouts[0].id));
    }
  }, [layouts, state.selectedLayoutId, setSelectedLayoutIdAction]);

  useEffect(() => {
    const selectedLayout = layouts.find((l: Layout) => String(l.id) === state.selectedLayoutId);
    if (!selectedLayout) {
      lastLoadedLayoutRef.current = null;
      return;
    }

    const key = getLayoutAssetKey(selectedLayout);
    const lastLoaded = lastLoadedLayoutRef.current;
    if (lastLoaded?.key === key && lastLoaded.retryVersion === layoutAssetRetryVersion) return;

    lastLoadedLayoutRef.current = { key, retryVersion: layoutAssetRetryVersion };
    void loadLayout(selectedLayout);
  }, [state.selectedLayoutId, layouts, layoutAssetRetryVersion, loadLayout]);

  useEffect(() => {
    if (photoData) {
      setState((prev) => {
        const nextState: InteractiveViewerState = { ...prev, currentPhoto: photoData };
        if (photoData.layout_id != null) {
          const resolvedLayoutId = String(photoData.layout_id);
          if (resolvedLayoutId !== prev.selectedLayoutId) {
            nextState.selectedLayoutId = resolvedLayoutId;
            nextState.selectedUnit = null;
            nextState.imagePath = '';
            nextState.pdfPath = null;
            nextState.isPdfLayout = false;
            nextState.isLayoutAssetLoading = true;
          }
        }
        return nextState;
      });
    }
  }, [photoData]);

  // Update marker number when photo markers change (but not in photoIndexMode)
  useEffect(() => {
    if (!state.selectedLayoutId || state.photoIndexMode) return;

    if (photoMarkers.length > 0) {
      const maxNumber = Math.max(...photoMarkers.map((m: PhotoMarker) => m.number));
      setState((prev) => ({
        ...prev,
        currentMarkerNumber: maxNumber + 1,
      }));
    } else {
      setState((prev) => ({ ...prev, currentMarkerNumber: 1 }));
    }
  }, [state.selectedLayoutId, photoMarkers, state.photoIndexMode]);

  useEffect(() => {
    if (!state.selectedLayoutId || state.photoIndexMode) return;
    setState((prev) => ({ ...prev, localPhotoMarkers: photoMarkers }));
  }, [state.selectedLayoutId, photoMarkers, state.photoIndexMode]);

  // Initialize history and marker attributes when entering photoIndexMode
  useEffect(() => {
    if (!state.selectedLayoutId) return; // Don't run if no layout selected
    if (state.photoIndexMode && state.markerHistory.length === 0) {
      // Save initial state to history
      const updates: Partial<InteractiveViewerState> = {
        markerHistory: [[...state.localPhotoMarkers]],
        historyIndex: 0,
      };

      // Set currentMarkerNumber based on existing markers
      if (photoMarkers.length > 0) {
        // If there are existing markers, start at max + 1
        const maxNumber = Math.max(...photoMarkers.map((m: PhotoMarker) => m.number));
        updates.currentMarkerNumber = maxNumber + 1;

        // Find the largest width and height across all markers
        const maxWidth = Math.max(...photoMarkers.map((m: PhotoMarker) => m.width || 30));
        const maxHeight = Math.max(...photoMarkers.map((m: PhotoMarker) => m.height || 18));
        const maxFontSize = Math.max(...photoMarkers.map((m: PhotoMarker) => m.fontSize || 12));

        updates.markerAttributes = {
          width: maxWidth,
          height: maxHeight,
          fontSize: maxFontSize,
          aspectRatio: maxWidth / maxHeight,
        };
      } else {
        // No existing markers, start at 1
        updates.currentMarkerNumber = 1;
      }

      setState((prev) => ({ ...prev, ...updates }));
    }
  }, [
    state.selectedLayoutId,
    state.photoIndexMode,
    state.markerHistory.length,
    state.localPhotoMarkers,
    photoMarkers,
  ]);

  // Handle marker dragging
  useEffect(() => {
    // Attach listeners when drag starts (dragStartPosition is set), not when draggingMarkers is true
    if (!state.dragStartPosition) return;

    const handleMouseMove = (event: MouseEvent) => {
      actions.handleMarkerDrag(event);
    };

    const handleMouseUp = () => {
      actions.handleMarkerDragEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [state.dragStartPosition, actions]);

  return {
    state,
    actions,
    refs: {
      svgRef,
      groupRef,
      dragStartRef,
      lastDragPointRef,
      dragStartTimeRef,
      touchStartDistanceRef,
      touchStartScaleRef,
    },
    data: {
      layouts,
      areaTags,
      photoMarkers,
      availablePhotos,
      photoData,
      hotspotsData,
      layoutHotspots,
      displayPhotoMarkers,
      layoutsLoading,
      layoutsError,
      refetchLayouts,
      layoutDataLoading,
      layoutDataFetching,
      isLayoutAssetLoading: state.isLayoutAssetLoading,
      markerAutosave,
    },
    mutations: {
      createAreaTagMutation,
      savePhotoMarkersMutation,
    },
  };
};
